import { useState } from 'react';
import { PageLayoutsContainer } from '@wajeez-web/components/layouts';
import { HandleTableData } from '@wajeez-web/components/data-table';
import { AdminSystemPermissions } from '@wajeez-web/core/types';
import {
  useProductSelector,
  PromoCodesActions,
} from '@wajeez-web/stores/product';
import { Button } from '@wajeez-web/components/ui';
import { PlusIcon } from '@heroicons/react/solid';
import { DataTableWrapper, Header } from '@wajeez-web/components/helpers';
import { contentEntityDataType } from '@wajeez-web/stores/content';
import { Server } from '@wajeez/wajeez.microservices';

import { PromoCodeModal } from '../components/PromoCodeModal';
import { AssignPromoCodeToProductGroupModal } from '../components/AssignPromoCodeToProductGroupModal';

const PromoCodesPage = () => {
  const [isPromoCodeModalOpen, setIsPromoCodeModalOpen] = useState(false);
  const [
    isAssignPromoCodeToProductGroupModalOpen,
    setIsAssignPromoCodeToProductGroupModalOpen,
  ] = useState(false);
  const [selectedPromoCode, setSelectedPromoCode] =
    useState<Partial<Server.PromoCodeOutput>>();

  const datatable = useProductSelector(
    ({ promoCodes }) => promoCodes.promoCodesDatatable
  );

  const { tablePreferances, setTablePreferences, isTableReferencesLoading } =
    HandleTableData(
      'promoCodes',
      PromoCodesActions.getPromoCodesDatatableAsync
    );

  return (
    <div>
      <PageLayoutsContainer
        title="Promo Codes"
        endSideComponent={[
          {
            element: (
              <Button
                title={'Create Promo Code'}
                bg={'primary'}
                color={'white'}
                Icon={PlusIcon}
                onClick={() => {
                  setIsPromoCodeModalOpen(true);
                }}
              />
            ),
            permission: [AdminSystemPermissions.promoCode.add],
          },
        ]}
        withoutRightPadding={true}
        breadCrumbsLinks={[{ title: 'Promo Codes' }]}
      >
        <DataTableWrapper
          data={datatable.data as contentEntityDataType}
          isLoading={datatable.status === 'loading' || isTableReferencesLoading}
          onHeaderManage={(header) => {
            const flags = header
              ?.filter((h) => h.selected)
              .reduce(
                (previousValue: number, currentValue: Header) =>
                  previousValue + Number(currentValue.value),
                0
              );

            tablePreferances &&
              flags &&
              setTablePreferences({
                ...tablePreferances,
                flags: flags + 1,
              });
          }}
          onPaginationChange={(pagination) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                pageNumber: pagination.pageNumber,
                pageSize: pagination.pageSize,
              });
          }}
          sort={tablePreferances?.sortingOperations?.[0]}
          onSort={(sort) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                sortingOperations: [sort],
              });
          }}
          onFilter={(filters, pagination) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                pageNumber: pagination.pageNumber,
                filteringOperations: filters.map((f) => {
                  return {
                    property: Number(f.key),
                    cretiria: f.value,
                    comparisonType: f.operator,
                  };
                }),
              });
          }}
          rowActions={(row) => [
            {
              title: 'Edit',
              permission: AdminSystemPermissions.promoCode.edit,
              type: 'request',
              endpoint: '',
              action: () => {
                setSelectedPromoCode({
                  id: Number(row[1]),
                });
                setIsPromoCodeModalOpen(true);
              },
            },
            {
              title: 'Assign to Product Group',
              permission: AdminSystemPermissions.promoCode.assignProductGroup,
              type: 'request',
              endpoint: '',
              action: () => {
                setSelectedPromoCode({
                  id: Number(row[1]),
                });
                setIsAssignPromoCodeToProductGroupModalOpen(true);
              },
            },
          ]}
          preferences={tablePreferances || undefined}
          setPreferences={setTablePreferences}
        />
      </PageLayoutsContainer>
      {isPromoCodeModalOpen && (
        <PromoCodeModal
          isOpen={isPromoCodeModalOpen}
          onClose={() => {
            setIsPromoCodeModalOpen(false);
            setSelectedPromoCode(undefined);
          }}
          onSubmit={() => {
            setIsPromoCodeModalOpen(false);

            if (selectedPromoCode?.id) {
              setSelectedPromoCode(undefined);
            }

            tablePreferances && setTablePreferences({ ...tablePreferances });
          }}
          id={selectedPromoCode?.id}
        />
      )}
      {isAssignPromoCodeToProductGroupModalOpen && (
        <AssignPromoCodeToProductGroupModal
          isOpen={isAssignPromoCodeToProductGroupModalOpen}
          onClose={() => {
            setIsAssignPromoCodeToProductGroupModalOpen(false);
            setSelectedPromoCode(undefined);
          }}
          onSubmit={() => {
            setIsAssignPromoCodeToProductGroupModalOpen(false);

            if (selectedPromoCode?.id) {
              setSelectedPromoCode(undefined);
            }

            tablePreferances && setTablePreferences({ ...tablePreferances });
          }}
          id={selectedPromoCode?.id}
        />
      )}
    </div>
  );
};

export default PromoCodesPage;
